import React, { useEffect, useMemo, useState } from 'react';
import { pushEvent } from '../../../../utils/clevertapUtils';
import { VIEW_ORG_SEARCH_PAGE } from '../../../../utils/clevertapEvents';
import { PATH } from '../../../../routes';
import { useRouteConfig } from '../../utils/useRouteConfig';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../store';
import Header from '../../components/Header';
import Typography from '@material-ui/core/Typography/Typography';
import { InfoRow, maxSearchResultWidth } from '../InfoRow';
import { useWebSocket } from '../../utils/useWebSocket';
import { webConfig } from '../../../../webConfig';
import { onMessage } from '../../utils/organizationAutoCompleteUtils';
import { dataOrUndefined } from '../../utils/useRequestState';
import SearchLoading from '../../components/SearchLoading';
import OrganizationSearchField from './OrganizationSearchField';
import { useNavigate } from 'react-router-dom';
import OrganizationSearchModal from './OrganizationSearchModal';
import { scrollBarStyles } from '../../components/styles';
import { Features } from 'store/featureFlags/types';
import { styled } from '@mui/material';
import { SEARCH_EXPERIMENT, SEARCH_UI_EXPERIMENT } from 'utils/clevertapVariantFlags';
import { useFullStoryIdentity } from 'pages/signup/utils/useFullStoryIdentity';

const OrganizationGrid = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    [theme.breakpoints.down('sm')]: {
        '& .heading': {
            display: 'none',
        },
    },
    '& .heading': {
        textAlign: 'center',
        margin: theme.spacing(3, 0),
    },
    '& .bottom-grid': {
        ...scrollBarStyles,
        width: '100%',
        maxWidth: maxSearchResultWidth,
        margin: '0 auto',
        flex: '1 1 auto',
        overflow: 'auto',
    },
}));

const cutoffWidth = 600;
const HeaderContent: React.FC<{ previous: string }> = ({ previous }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);
    const heading = useMemo(() => t('signup.organizationName.heading'), [t]);
    const onClose = () => setOpen(false);
    const smallerThanCutoffWidth = window.innerWidth < cutoffWidth;
    const onClickTextField = () => {
        if (open === false && smallerThanCutoffWidth) {
            setOpen(true);
        }
    };

    return (
        <>
            <Header backUrl={previous} showCloseButton={true} />
            <Typography variant="h2" className="heading">
                {heading}
            </Typography>
            <div onClick={onClickTextField}>
                <OrganizationSearchModal open={open} onClose={onClose} />
                <OrganizationSearchField readOnly={smallerThanCutoffWidth} />
            </div>
        </>
    );
};

const OrganizationSearch: React.FC = () => {
    const push = useNavigate();
    const { previous } = useRouteConfig(PATH.SIGNUP.STEPS.SEARCH_ORGANIZATION);

    const { fingerprint, fullStoryEnabled, newSearchUIEnabled, organizationName, organizationLocation, v2SearchAPIEnabled } =
        useSelector((state: AppState) => ({
            fingerprint: state.Signup.fingerprint,
            fullStoryEnabled: state.FeatureFlagging.features[Features.FULLSTORY],
            newSearchUIEnabled: state.FeatureFlagging.features[Features.SEARCH_UI_V2],
            organizationName: state.Signup.organizationName,
            organizationLocation: state.Signup.organizationLocation,
            v2SearchAPIEnabled: state.FeatureFlagging.features[Features.SEARCH_API_V2],
        }));

    const [suggestionsState, makeRequest] = useWebSocket(
        v2SearchAPIEnabled
            ? webConfig.newOrganizationSearchUrl
            : webConfig.organizationSearchUrl,
        onMessage,
    );
    useEffect(() => {
        makeRequest(organizationName, organizationLocation, undefined);
    }, [organizationName, organizationLocation, makeRequest]);
    useEffect(() => {
        pushEvent(VIEW_ORG_SEARCH_PAGE, { 
            organizationName,
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off',
        });
    }, [organizationName, newSearchUIEnabled, v2SearchAPIEnabled]);
    useFullStoryIdentity({
        enabled: fullStoryEnabled,
        fingerprint,
    });

    if (organizationName.length < 3) {
        push(previous);
        return null;
    }
    const total = dataOrUndefined(suggestionsState)?.length || 0;
    return (
        <OrganizationGrid>
            <div className="top-grid">
                <HeaderContent previous={previous} />
                <InfoRow total={total} organizationName={organizationName} />
            </div>
            <div className="bottom-grid">
                <SearchLoading
                    requestStatus={suggestionsState}
                    total={total}
                    term={organizationName}
                />
            </div>
        </OrganizationGrid>
    );
};

export default OrganizationSearch;
